'use client'

import { useQuery } from '@tanstack/react-query'
import { useChannelsQuery } from '@/hooks/useChannelsQuery'
import { useProfilePicture } from '@/hooks/useProfilePicture'
import { useHasPermission } from '@/hooks/usePermissions'
import { getChannelPhoneProfile } from '@/services/channels/phoneProfile'
import type { ChannelWithStats } from '@/types/channel'

/**
 * Gate de `GET /whatsapp/channels/{id}/phone-profile` no back (`get_channel_phone_profile`): a permission E o
 * canal no escopo. É a leitura do atendente; `channels:phones:read` (a lista do tenant) é gestão.
 */
export const CHANNEL_PHONE_PROFILE_PERMISSION = 'channels:profile:read'

/**
 * Prefixo `['channels']` de propósito, como o roster (`useChannelRosterQuery`): `invalidateQueries(['channels'])`
 * casa por prefixo e relê junto. `all` é o que as mutations de perfil do admin invalidam (`useAdminPhones`).
 */
export const channelPhoneProfileKeys = {
  all: ['channels', 'phone-profile'] as const,
  byChannel: (channelId: string) => [...channelPhoneProfileKeys.all, channelId] as const,
}

export interface ChannelPhoneIdentity {
  verifiedName: string
  displayPhoneNumber: string
}

export interface ChannelIdentity {
  /** Nome do canal na lista HTTP; `''` enquanto a lista nao resolve. */
  name: string
  /** Slug lucide kebab; null = sem icone (ChannelAvatar cai em Antenna). */
  icon: string | null
  /** Hex #RRGGBB; null = sem cor. */
  color: string | null
  /** Hash opaco do numero. NUNCA renderizar. `''` enquanto a lista nao resolve. */
  phoneHashId: string
  /** null = sem permissao, canal fora do escopo/sem numero (403/404), ou numero indeterminado. */
  phone: ChannelPhoneIdentity | null
  /** Object URL da foto do numero; undefined = sem foto (404) ou erro. */
  photoSrc: string | undefined
}

/**
 * Fora de canal (home), o canal cujo número vale pro tenant visível: só quando TODO canal escopado com número
 * aponta pro MESMO `phone_hash_id`. Canal sem número (`''`) não vota — senão casaria `'' === ''`. Dois números
 * → null, sem chutar.
 */
function canalDoNumeroUnico(channels: readonly ChannelWithStats[]): string | null {
  const comNumero = channels.filter((c) => c.phone_hash_id !== '')
  const numeros = new Set(comNumero.map((c) => c.phone_hash_id))
  return numeros.size === 1 ? comNumero[0].db_id : null
}

/**
 * Identidade de um canal + do numero que o serve, de tres origens que ja existem.
 * O número sai da rota POR CANAL, então multi-phone já resolve por canal; o que não resolve é a foto
 * (`useProfilePicture`, uma por tenant).
 *
 * `lerNumero: false` desliga só a rota do número (`phone` fica null): pra quem já tem o número por outra fonte e
 * descartaria a resposta (o admin de canal com a lista do tenant). Nome/ícone/cor/foto seguem iguais.
 */
export function useChannelIdentity(
  channelId: string | null | undefined,
  { lerNumero = true }: { lerNumero?: boolean } = {},
): ChannelIdentity {
  const canReadProfile = useHasPermission(CHANNEL_PHONE_PROFILE_PERMISSION)
  const { data: channels } = useChannelsQuery()
  const { src, isError } = useProfilePicture()

  // `data ?? []`: useChannelsQuery nao tem fallback [] na 1a falha sem cache.
  const list = channels ?? []
  const channel = list.find((c) => c.db_id === channelId) ?? null
  // Canal dado não espera a lista: a rota é por canal e o escopo quem decide é o back (fora dele, 403).
  const alvo = channelId || canalDoNumeroUnico(list)

  const { data: profile } = useQuery({
    queryKey: channelPhoneProfileKeys.byChannel(alvo ?? ''),
    queryFn: () => getChannelPhoneProfile(alvo ?? ''),
    // Sem o gate, 403 a cada mount de tela privada pra quem nao tem a permission.
    enabled: canReadProfile && alvo !== null && lerNumero,
    // 403 (fora do escopo) e 404 (canal/número) são estado, não falha transiente: a UI degrada pra phone: null.
    retry: false,
    staleTime: 5 * 60_000,
  })

  return {
    name: channel?.name ?? '',
    icon: channel?.icon ?? null,
    color: channel?.color ?? null,
    phoneHashId: channel?.phone_hash_id ?? '',
    // `lerNumero &&`: com `enabled: false` a TanStack ainda devolve o data que já está em cache (outra tela leu a key).
    phone:
      lerNumero && profile
        ? // contrato-back: os dois podem vir `null`; consumidores já tratam '' como ausente
          { verifiedName: profile.verified_name ?? '', displayPhoneNumber: profile.display_phone_number ?? '' }
        : null,
    photoSrc: isError ? undefined : src,
  }
}
